import dayjs, { Dayjs } from 'dayjs'
import { birthDayOf } from '../../../utils/birthDay'
import { Scope } from '../../../utils/constants'

/** The /birthdays fields a birthday wish is built from. */
export interface BirthdayWishTarget {
  fullName: string
  /** ISO date; read with birthDayOf, as in the list. */
  birthDate: string
  scope?: string
}

type Translate = (key: string, params?: Record<string, string | number>) => string

/** Up to this many days before/after the date, the wish is "early"/"belated". */
export const NEAR_BIRTHDAY_DAYS = 7

const AI_LANGUAGE_NAMES: Record<string, string> = {
  en: 'anglais',
  es: 'espagnol',
}

export type BirthdayTiming = 'today' | 'early' | 'belated' | 'other'

export interface BirthdayMoment {
  timing: BirthdayTiming
  /** Days until (early) or since (belated) the birthday; 0 otherwise. */
  days: number
  /** Set only for a round age (18, 20, 30…) reached on that birthday. */
  milestoneAge?: number
}

const isMilestone = (age: number) => age === 18 || (age >= 20 && age <= 100 && age % 10 === 0)

/**
 * Where today stands against the person's birthday. A far-off birthday is
 * "other": its wish stays plain, with no age (which occurrence is meant is
 * unknown).
 */
export function getBirthdayMoment(birthDate: string, now: Dayjs = dayjs()): BirthdayMoment {
  const today = now.startOf('day')
  const born = dayjs(birthDayOf(birthDate), 'YYYY-MM-DD')
  let next = born.year(today.year())
  if (next.isBefore(today, 'day')) next = born.year(today.year() + 1)
  const previous = next.subtract(1, 'year')
  const daysUntil = next.diff(today, 'day')
  const daysSince = today.diff(previous, 'day')

  let timing: BirthdayTiming = 'other'
  let occurrence = next
  if (daysUntil === 0) timing = 'today'
  else if (daysUntil <= NEAR_BIRTHDAY_DAYS) timing = 'early'
  else if (daysSince <= NEAR_BIRTHDAY_DAYS) {
    timing = 'belated'
    occurrence = previous
  }

  const age = occurrence.year() - born.year()
  return {
    timing,
    days: timing === 'early' ? daysUntil : timing === 'belated' ? daysSince : 0,
    milestoneAge: timing !== 'other' && isMilestone(age) ? age : undefined,
  }
}

const isProfessional = (scope?: string) => scope?.toUpperCase() === Scope.PROFESSIONAL

/**
 * Instruction for the conversation AI (POST /ft_conversation-ai/generate),
 * which already reads the last messages of the conversation: the wish is
 * personalised from what the two people talked about, never from profile
 * data (hashtags read as fake), and invents nothing.
 */
export function birthdayWishInstruction(
  target: BirthdayWishTarget,
  now: Dayjs,
  language: string,
): string {
  const { timing, days, milestoneAge } = getBirthdayMoment(target.birthDate, now)
  const plural = days > 1 ? 's' : ''
  const lines = [
    `Écris un message pour souhaiter un joyeux anniversaire à ${target.fullName.trim()}, en l'appelant par son prénom.`,
  ]

  if (timing === 'today') lines.push("C'est son anniversaire aujourd'hui.")
  if (timing === 'early') {
    lines.push(`Son anniversaire est dans ${days} jour${plural} : souhaite-le en avance.`)
  }
  if (timing === 'belated') {
    lines.push(`Son anniversaire était il y a ${days} jour${plural} : souhaite-le avec un peu de retard.`)
  }
  if (milestoneAge) lines.push(`La personne fête ses ${milestoneAge} ans : tu peux le dire simplement.`)
  lines.push(
    "Appuie-toi sur l'historique de notre conversation : si nous avons parlé récemment d'un sujet personnel (un projet, un voyage, une bonne nouvelle…), fais-y un clin d'œil naturel. Évite les sujets tristes ou délicats. S'il n'y a rien à reprendre, reste simple.",
    "N'invente rien : aucun souvenir, projet ou détail qui ne figure pas dans nos échanges.",
    isProfessional(target.scope)
      ? 'Ton cordial et sobre, en vouvoyant, sauf si nous nous tutoyons dans nos échanges.'
      : 'Ton naturel et chaleureux, comme un message à un proche, en tutoyant, sauf si nous nous vouvoyons dans nos échanges.',
    "Court : une à trois phrases, comme un vrai message écrit par moi. Un emoji au plus. Pas de hashtags, pas de formule toute faite, pas de signature.",
    "Les trois suggestions doivent toutes être des messages d'anniversaire.",
  )
  const languageName = AI_LANGUAGE_NAMES[language]
  if (languageName) lines.push(`Écris en ${languageName}, sauf si nos échanges sont dans une autre langue.`)
  return lines.join('\n')
}

/**
 * The conversation AI returns short / medium / long suggestions: keep the
 * medium one. Its raw answer comes back as the only suggestion when it is not
 * valid JSON; that one is dropped if it still looks like JSON.
 */
export function pickBirthdayWish(suggestions: unknown): string | undefined {
  if (!Array.isArray(suggestions)) return undefined
  const usable = suggestions
    .filter((s): s is string => typeof s === 'string')
    .map((s) => s.trim())
    .filter((s) => s && !/^[[{]/.test(s) && !s.includes('"suggestions"'))
    .sort((a, b) => a.length - b.length)
  return usable[Math.floor(usable.length / 2)]
}

const firstNameOf = (fullName: string) => fullName.trim().split(/\s+/)[0] ?? ''

/**
 * The wish written without the AI (not configured, down, too slow): short
 * and plain on purpose. First name, tu/vous from Privé/Pro, early/belated,
 * and a round age for a private contact.
 */
export function birthdayWishFallback(target: BirthdayWishTarget, now: Dayjs, t: Translate): string {
  const { timing, milestoneAge } = getBirthdayMoment(target.birthDate, now)
  const firstName = firstNameOf(target.fullName)
  const isPro = isProfessional(target.scope)
  const openingKey =
    timing === 'early' ? 'openingEarly' : timing === 'belated' ? 'openingBelated' : 'opening'
  const parts = [t(`birthdaysScreen.wish.${openingKey}`, { firstName })]

  if (milestoneAge && !isPro) parts.push(t('birthdaysScreen.wish.milestone', { age: milestoneAge }))
  const bodyKey = `${isPro ? 'pro' : 'private'}${timing === 'belated' ? 'Belated' : ''}`
  parts.push(t(`birthdaysScreen.wish.${bodyKey}`))
  return parts.join(' ')
}
